// Client-side FUTRA Identity Report generator — composes a shareable PNG scorecard.
const loadImg = (src) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => res(img);
  img.onerror = rej;
  img.src = src;
});

const drawCover = (ctx, img, x, y, w, h) => {
  const r = Math.max(w / img.width, h / img.height);
  const nw = img.width * r, nh = img.height * r;
  ctx.save();
  ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
  ctx.drawImage(img, x + (w - nw) / 2, y + (h - nh) / 2, nw, nh);
  ctx.restore();
};

export async function downloadIdentityReport({ image, identity, prompt = "" }) {
  const W = 920, H = 620;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#0a0a0c"; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(212,175,55,0.4)"; ctx.lineWidth = 2; ctx.strokeRect(12, 12, W - 24, H - 24);

  ctx.fillStyle = "#d4af37"; ctx.font = "bold 30px Arial";
  ctx.fillText("FUTRA™ Identity Report", 44, 60);
  ctx.fillStyle = "rgba(255,255,255,0.4)"; ctx.font = "12px monospace";
  ctx.fillText("BIOMETRIC · InsightFace ArcFace 512-d", 44, 84);

  const lock = identity.status === "LOCKED";
  ctx.fillStyle = lock ? "#39ff14" : "#ff007f";
  ctx.font = "bold 78px Arial"; ctx.fillText(`${identity.overall}%`, 44, 190);
  ctx.font = "bold 20px Arial"; ctx.fillText(identity.status, 46, 220);

  try {
    if (image) { const img = await loadImg(image); drawCover(ctx, img, W - 360, 44, 320, 320); }
  } catch (e) { /* image optional */ }

  let y = 268; ctx.font = "13px monospace";
  Object.entries(identity.scores || {}).forEach(([k, v]) => {
    ctx.fillStyle = "rgba(255,255,255,0.55)"; ctx.fillText(k, 44, y);
    ctx.fillStyle = "rgba(255,255,255,0.1)"; ctx.fillRect(44, y + 8, 420, 8);
    ctx.fillStyle = v >= 75 ? "#39ff14" : "#ff007f"; ctx.fillRect(44, y + 8, (420 * v) / 100, 8);
    ctx.fillStyle = "#fff"; ctx.fillText(`${v}%`, 474, y + 12);
    y += 36;
  });

  if (identity.flags?.length) {
    ctx.fillStyle = "#ff007f"; ctx.font = "11px monospace";
    ctx.fillText("Flags: " + identity.flags.join(" · "), 44, H - 60);
  }
  ctx.fillStyle = "rgba(255,255,255,0.35)"; ctx.font = "11px monospace";
  ctx.fillText(`Cosine ${identity.cosine} · Generated by FUTRA™ · ${new Date().toLocaleDateString()}`, 44, H - 34);

  const a = document.createElement("a");
  a.href = canvas.toDataURL("image/png");
  a.download = `futra-identity-report-${identity.overall}pct.png`;
  a.click();
}

export async function downloadGroupReport({ image, report, name = "Group" }) {
  const W = 1000, H = 640;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#0a0a0c"; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(212,175,55,0.4)"; ctx.lineWidth = 2; ctx.strokeRect(12, 12, W - 24, H - 24);

  ctx.fillStyle = "#d4af37"; ctx.font = "bold 28px Arial";
  ctx.fillText("FUTRA\u2122 Group Identity Report", 44, 58);
  ctx.fillStyle = "rgba(255,255,255,0.5)"; ctx.font = "13px monospace";
  ctx.fillText(name, 44, 82);

  const lock = report.status === "LOCKED";
  ctx.fillStyle = lock ? "#39ff14" : "#ff007f";
  ctx.font = "bold 64px Arial"; ctx.fillText(`${report.group_score}%`, 44, 156);
  ctx.font = "bold 16px Arial"; ctx.fillText(`${report.status} \u00b7 ${report.detected}/${report.expected} PEOPLE`, 46, 184);

  try { if (image) { const img = await loadImg(image); drawCover(ctx, img, W - 400, 40, 360, 360); } } catch (e) { /* group image optional */ }

  let y = 220;
  for (const p of (report.people || [])) {
    try { if (p.crop) { const ci = await loadImg(p.crop); ctx.drawImage(ci, 44, y - 22, 34, 34); } } catch (e) { /* person crop optional */ }
    ctx.fillStyle = "rgba(255,255,255,0.8)"; ctx.font = "14px monospace";
    ctx.fillText(`${p.name}${p.is_child ? " (child)" : ""}`, 88, y - 4);
    ctx.fillStyle = "rgba(255,255,255,0.1)"; ctx.fillRect(88, y + 4, 300, 7);
    ctx.fillStyle = p.status === "LOCKED" ? "#39ff14" : "#ff007f"; ctx.fillRect(88, y + 4, (300 * p.score) / 100, 7);
    ctx.fillStyle = "#fff"; ctx.fillText(`${p.score}%`, 400, y + 6);
    y += 44;
  }

  if (report.warnings && report.warnings.length) {
    ctx.fillStyle = "#ff007f"; ctx.font = "11px monospace";
    ctx.fillText("\u26a0 " + report.warnings.slice(0, 3).join(" \u00b7 "), 44, H - 58);
  }
  ctx.fillStyle = "rgba(255,255,255,0.35)"; ctx.font = "11px monospace";
  ctx.fillText(`Multi-Identity Orchestration \u00b7 InsightFace ArcFace \u00b7 FUTRA\u2122 \u00b7 ${new Date().toLocaleDateString()}`, 44, H - 34);

  const a = document.createElement("a");
  a.href = canvas.toDataURL("image/png");
  a.download = `futra-group-report-${report.group_score}pct.png`;
  a.click();
}
